/**
 * ImageUpload component
 * Handles image selection (file picker (gallery, camera, files)), validation, HEIC conversion,
 * preview, and base64 encoding before calling onImageReady.
 *
 * AC coverage:
 *   AC-003.1 - Accepts JPEG, PNG, HEIC under 10MB; shows file picker + drag-drop zone
 *   AC-003.2 - HEIC files converted to JPEG client-side via heic2any before upload
 *   AC-003.5 - Files > 10MB rejected with error before any network request
 *   AC-003.6 - Non-image files rejected with "Please upload a JPEG or PNG image"
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import heic2any from 'heic2any';
import { Camera, X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB

const ACCEPTED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
]);

const ACCEPTED_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.heic', '.heif']);

export interface ImageUploadProps {
  /** Called when an image is ready to send (validated, converted, base64-encoded) */
  onImageReady: (base64Data: string, mimeType: string, previewUrl: string) => void;
  /** Disables all interaction when true */
  disabled?: boolean;
}

/**
 * Detects whether a file is HEIC/HEIF by MIME type or extension.
 */
function isHeicFile(file: File): boolean {
  const mimeType = file.type.toLowerCase();
  if (mimeType === 'image/heic' || mimeType === 'image/heif') return true;
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return ext === '.heic' || ext === '.heif';
}

/**
 * Checks whether a file's MIME type or extension is an accepted image type.
 */
function isAcceptedImageType(file: File): boolean {
  const mimeType = file.type.toLowerCase();
  if (ACCEPTED_MIME_TYPES.has(mimeType)) return true;
  // Fallback to extension check (some browsers don't set MIME for HEIC)
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return ACCEPTED_EXTENSIONS.has(ext);
}

/**
 * ImageUpload - File picker with drag-drop, HEIC conversion, preview, and base64 encoding.
 */
export function ImageUpload({ onImageReady, disabled = false }: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedBlob, setSelectedBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isEncoding, setIsEncoding] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Cleanup preview URL on unmount or when selection changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  /**
   * Process a File through: validation -> HEIC conversion -> preview URL creation.
   * Does NOT encode to base64 yet — that happens only when the user clicks "Send".
   */
  const processFile = useCallback(
    async (file: File) => {
      setError(null);

      // Step 1: Size validation (AC-003.5) — must be BEFORE any network request
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError('Image must be under 10MB');
        return;
      }

      // Step 2: Type validation (AC-003.6)
      if (!isAcceptedImageType(file)) {
        setError('Please upload a JPEG or PNG image');
        return;
      }

      let finalBlob: Blob = file;
      let finalMimeType = file.type || 'image/jpeg';

      // Step 3: HEIC conversion (AC-003.2)
      if (isHeicFile(file)) {
        setIsConverting(true);
        try {
          const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
          // heic2any can return Blob or Blob[] depending on options
          finalBlob = Array.isArray(result) ? result[0] : result;
          finalMimeType = 'image/jpeg';
        } catch {
          setIsConverting(false);
          setError(
            'This image format is not supported. Please save or convert to JPEG or PNG and try again.'
          );
          return;
        }
        setIsConverting(false);
      }

      // Step 4: Revoke any existing preview URL to free memory
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      const newPreviewUrl = URL.createObjectURL(finalBlob);
      setSelectedBlob(finalBlob);
      setPreviewUrl(newPreviewUrl);
      // Store mime type on the blob for use during send
      Object.defineProperty(finalBlob, '_mimeType', { value: finalMimeType, writable: false });
    },
    [previewUrl]
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void processFile(file);
    }
    // Reset input so the same file can be re-selected after cancel
    e.target.value = '';
  };

  // ---- Drag and Drop (AC-003.1) ----

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void processFile(file);
    }
  };

  // ---- Cancel / Reset ----

  const handleCancel = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedBlob(null);
    setPreviewUrl(null);
    setError(null);
  };

  // ---- Send: base64 encode then call onImageReady ----

  const handleSend = () => {
    if (!selectedBlob || !previewUrl) return;

    setIsEncoding(true);
    const mimeType =
      (selectedBlob as Blob & { _mimeType?: string })._mimeType ||
      (selectedBlob.type !== '' ? selectedBlob.type : 'image/jpeg');

    const reader = new FileReader();

    reader.onload = () => {
      setIsEncoding(false);
      const dataUrl = reader.result as string;
      // Strip "data:image/jpeg;base64," prefix to get raw base64 string
      const base64Data = dataUrl.split(',')[1];
      if (!base64Data) {
        setError('Failed to encode image. Please try again.');
        return;
      }
      // Pass previewUrl so the hook can use it for the optimistic message display
      onImageReady(base64Data, mimeType, previewUrl);
      // Reset component state after successful send
      setSelectedBlob(null);
      setPreviewUrl(null);
    };

    reader.onerror = () => {
      setIsEncoding(false);
      setError('Failed to encode image. Please try again.');
    };

    reader.readAsDataURL(selectedBlob);
  };

  const isProcessing = isConverting || isEncoding;
  const isDisabled = disabled || isProcessing;

  // ---- Render: Preview mode ----
  if (previewUrl && selectedBlob) {
    return (
      <div className="flex flex-col gap-2 p-2">
        {/* Image preview card */}
        <div className="relative border border-[#E7D8C8] rounded-lg overflow-hidden bg-otter-sand max-h-48">
          <img
            src={previewUrl}
            alt="Selected question"
            className="max-w-full max-h-48 object-contain mx-auto block"
          />
        </div>

        {/* Preview action buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isProcessing}
            className="flex-1 h-11"
            aria-label="Cancel image selection"
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSend}
            disabled={isDisabled}
            className="flex-1 h-11"
            aria-label="Send image"
          >
            {isEncoding ? (
              <>
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-1" />
                Send
              </>
            )}
          </Button>
        </div>

        {/* Error display */}
        {error && (
          <div className="text-sm text-red-600" role="alert" aria-live="polite">
            {error}
          </div>
        )}
      </div>
    );
  }

  // ---- Render: Upload picker mode ----
  return (
    <div className="flex flex-col gap-1">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif"
        className="hidden"
        onChange={handleFileInputChange}
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Drag-and-drop zone + upload button */}
      <div
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex items-center justify-center',
          isDraggingOver && 'ring-2 ring-violet-400 ring-offset-1 rounded-lg'
        )}
      >
        {isConverting ? (
          <Button
            variant="outline"
            size="sm"
            disabled
            className="h-11 px-3"
            aria-label="Converting image..."
          >
            <Loader2 className="h-5 w-5 animate-spin text-violet-500" />
            <span className="sr-only">Converting...</span>
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isDisabled}
            className="h-11 px-3"
            aria-label="Upload question photo"
            title="Upload a photo of the math question"
          >
            <Camera className="h-5 w-5 text-violet-500" />
          </Button>
        )}
      </div>

      {/* Validation error (outside preview mode) */}
      {error && !previewUrl && (
        <div className="text-xs text-red-600 max-w-[160px]" role="alert" aria-live="polite">
          {error}
        </div>
      )}
    </div>
  );
}
